import React from 'react';
import PropTypes from 'prop-types';

import SrBadge, {
  SR_BADGE_THEME_GREY,
  SR_BADGE_THEME_ORANGE,
  SR_BADGE_THEME_BLUE,
  SR_BADGE_THEME_GREEN,
  SR_BADGE_THEME_RED,
} from 'client/components/SrBadge/SrBadge';

import {
  ON_SWITCH_WORKFLOW_RUN_STATUS_PENDING,
  ON_SWITCH_WORKFLOW_RUN_STATUS_RUNNING,
  ON_SWITCH_WORKFLOW_RUN_STATUS_COMPLETED,
  ON_SWITCH_WORKFLOW_RUN_STATUS_FAILED,
  ON_SWITCH_WORKFLOW_RUN_STATUS_CANCELLED,
  ON_SWITCH_WORKFLOW_RUN_STATUS_NO_RUN,
} from 'common/utility/constants';

import { getOnSwitchWorkflowRunStatusLabel } from 'common/utility/onSwitchHelpers';

// Icon components (14x14, stroke-based SVGs)
const CircleMinusIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" />
    <path d="M8 12h8" />
  </svg>
);

const ClockIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" />
    <polyline points="12 6 12 12 16 14" />
  </svg>
);

const LoaderCircleIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 12a9 9 0 1 1-6.219-8.56" />
  </svg>
);

const CircleCheckIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" />
    <path d="m9 12 2 2 4-4" />
  </svg>
);

const CircleXIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" />
    <path d="m15 9-6 6" />
    <path d="m9 9 6 6" />
  </svg>
);

const CircleSlashIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" />
    <path d="M5.636 5.636 18.364 18.364" />
  </svg>
);

const ExternalLinkIcon = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
    <polyline points="15 3 21 3 21 9" />
    <line x1="10" y1="14" x2="21" y2="3" />
  </svg>
);

const STATUS_TO_THEME_MAPPING = {
  [ON_SWITCH_WORKFLOW_RUN_STATUS_NO_RUN]: SR_BADGE_THEME_GREY,
  [ON_SWITCH_WORKFLOW_RUN_STATUS_PENDING]: SR_BADGE_THEME_ORANGE,
  [ON_SWITCH_WORKFLOW_RUN_STATUS_RUNNING]: SR_BADGE_THEME_BLUE,
  [ON_SWITCH_WORKFLOW_RUN_STATUS_COMPLETED]: SR_BADGE_THEME_GREEN,
  [ON_SWITCH_WORKFLOW_RUN_STATUS_FAILED]: SR_BADGE_THEME_RED,
  [ON_SWITCH_WORKFLOW_RUN_STATUS_CANCELLED]: SR_BADGE_THEME_GREY,
};

const STATUS_TO_ICON_MAPPING = {
  [ON_SWITCH_WORKFLOW_RUN_STATUS_NO_RUN]: <CircleMinusIcon />,
  [ON_SWITCH_WORKFLOW_RUN_STATUS_PENDING]: <ClockIcon />,
  [ON_SWITCH_WORKFLOW_RUN_STATUS_RUNNING]: <LoaderCircleIcon />,
  [ON_SWITCH_WORKFLOW_RUN_STATUS_COMPLETED]: <CircleCheckIcon />,
  [ON_SWITCH_WORKFLOW_RUN_STATUS_FAILED]: <CircleXIcon />,
  [ON_SWITCH_WORKFLOW_RUN_STATUS_CANCELLED]: <CircleSlashIcon />,
};

const SrOnSwitchWorkflowStatusBadge = ({ status }) => {
  const theme = STATUS_TO_THEME_MAPPING[status] || SR_BADGE_THEME_GREY;
  const icon = STATUS_TO_ICON_MAPPING[status] || <CircleMinusIcon />;
  const label = getOnSwitchWorkflowRunStatusLabel(status);

  return <SrBadge theme={theme} icon={icon}>{label}</SrBadge>;
};

SrOnSwitchWorkflowStatusBadge.propTypes = {
  status: PropTypes.string.isRequired,
};

SrOnSwitchWorkflowStatusBadge.displayName = 'SrOnSwitchWorkflowStatusBadge';

export const SrOnSwitchFactoryLinkBadge = () => (
  <SrBadge theme={SR_BADGE_THEME_BLUE} icon={<ExternalLinkIcon />}>
    On Switch Factory
  </SrBadge>
);

SrOnSwitchFactoryLinkBadge.displayName = 'SrOnSwitchFactoryLinkBadge';

export default SrOnSwitchWorkflowStatusBadge;
